/**
 * Inbasket triage filter engine (OE-124) — pure logic, worker/jest-safe: no
 * DOM, no Angular, no service imports.
 *
 * ONE function feeds three surfaces: the rendered list, the live toggle counts
 * in the Find sheet/panel, and the Select-all scope. Semantics:
 *
 *   **OR within a group, AND across groups.**
 *
 * Groups are: text · read state · source · checklist status (done OR missed) ·
 * attachments · has-a-reply · older-than · from-person. Ticking both
 * "Checklist done" and "Missed deadline" therefore shows both sets rather than
 * their (near-empty) intersection.
 *
 * Two hard rules from CLAUDE.md are baked in:
 *  - "missed deadline" reads the SERVER-STAMPED `completionDateMissed` flag
 *    only — never date math (a particle's dates are generation times);
 *  - a missing/epoch `internalDate` is never treated as old, so a corrupt date
 *    can't get swept into a bulk action.
 */

import { SOURCE_FILTER_OPTIONS, filterParticlesBySource } from '../basket-base/source-filter';

export type TriageReadState = 'any' | 'unread' | 'read';
export type TriageOlderThan = null | '1w' | '1m' | '3m';

export interface TriageFilterable {
    particleKey?: string;
    particleType?: string;
    from?: string;
    subject?: string;
    snippet?: string;
    readStatus?: boolean;
    isComplete?: boolean;
    completionDateMissed?: boolean;
    attachments?: string[];
    attachmentsMeta?: Record<string, unknown>;
    internalDate?: { seconds?: number; _seconds?: number };
    /** Provider thread/conversation id — written at ingest (OE-125). */
    threadId?: string;
}

export interface TriageFilterState {
    /** Free text over sender / subject / snippet (AND across words). */
    text: string;
    read: TriageReadState;
    /** Source buckets (gmail / outlook / checklist / other). Empty = all. */
    sources: string[];
    checklistDone: boolean;
    checklistMissed: boolean;
    hasAttachments: boolean;
    /** A LATER particle shares this one's threadId (OE-125). */
    hasReply: boolean;
    olderThan: TriageOlderThan;
    /** Person chips — matched against the sender address. Empty = all. */
    fromEmails: string[];
}

export interface TriageChip {
    /** Stable id, also the handle passed to `removeTriageFilter`. */
    id: string;
    label: string;
}

export const EMPTY_TRIAGE_FILTER: TriageFilterState = Object.freeze({
    text: '',
    read: 'any' as TriageReadState,
    sources: Object.freeze([]) as unknown as string[],
    checklistDone: false,
    checklistMissed: false,
    hasAttachments: false,
    hasReply: false,
    olderThan: null,
    fromEmails: Object.freeze([]) as unknown as string[],
});

export const OLDER_THAN_LABELS: ReadonlyArray<{ key: Exclude<TriageOlderThan, null>; label: string; days: number }> = [
    { key: '1w', label: '1 week', days: 7 },
    { key: '1m', label: '1 month', days: 30 },
    { key: '3m', label: '3 months', days: 90 },
];

const DAY_MS = 86400000;

/** Seconds since epoch, or 0 when the particle carries no usable date. */
function dateSeconds(particle: TriageFilterable): number {
    const raw = particle?.internalDate;
    return raw?.seconds ?? raw?._seconds ?? 0;
}

function matchesText(particle: TriageFilterable, text: string): boolean {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;

    const haystack = [particle?.from, particle?.subject, particle?.snippet].filter(Boolean).join(' ').toLowerCase();

    return words.every((word) => haystack.includes(word));
}

function matchesRead(particle: TriageFilterable, read: TriageReadState): boolean {
    if (read === 'any') return true;
    // A missing readStatus means the particle was never marked read.
    const isRead = particle?.readStatus === true;
    return read === 'read' ? isRead : !isRead;
}

function matchesChecklist(particle: TriageFilterable, done: boolean, missed: boolean): boolean {
    if (!done && !missed) return true;
    // OR within the group.
    if (done && particle?.isComplete === true) return true;
    if (missed && particle?.completionDateMissed === true) return true;
    return false;
}

function matchesAttachments(particle: TriageFilterable): boolean {
    if ((particle?.attachments?.length ?? 0) > 0) return true;
    return Object.keys(particle?.attachmentsMeta || {}).length > 0;
}

function matchesOlderThan(particle: TriageFilterable, olderThan: TriageOlderThan, now: number): boolean {
    if (!olderThan) return true;

    const seconds = dateSeconds(particle);
    // A missing or epoch date is NOT "old" — corrupt dates must never be swept
    // into a bulk action (CLAUDE.md: epoch drift is a real failure mode here).
    if (!seconds || seconds <= 0) return false;

    const window = OLDER_THAN_LABELS.find((o) => o.key === olderThan);
    if (!window) return true;
    return seconds * 1000 <= now - window.days * DAY_MS;
}

/**
 * Addresses inside a `from` header (`"Ina <ina@x.co>"`, a bare address, or a
 * comma list). Person chips compare against these WHOLE addresses — a
 * substring match would make the chip "a@flash.co.za" also select mail from
 * "ina@flash.co.za".
 */
function addressesIn(raw: string | undefined): string[] {
    return ((raw || '').toLowerCase().match(/[^\s<>,;"']+@[^\s<>,;"']+/g) || []).map((a) => a.replace(/[.,;]+$/, ''));
}

function matchesFrom(particle: TriageFilterable, fromEmails: string[]): boolean {
    if (!fromEmails.length) return true;
    const addresses = addressesIn(particle?.from);
    if (!addresses.length) return false;
    return fromEmails.some((email) => {
        const needle = (email || '').trim().toLowerCase();
        return !!needle && addresses.includes(needle);
    });
}

/** Any particle carries a threadId — the Find toggle stays HIDDEN otherwise. */
export function anyThreadIds(particles: TriageFilterable[]): boolean {
    return (particles || []).some((p) => !!(p?.threadId || '').trim());
}

/**
 * Keys of particles that HAVE a reply: another particle in the list shares
 * their threadId with a strictly LATER internalDate. The newest member of a
 * thread (the reply itself) is not "replied to"; a missing threadId never
 * matches. Computed over the WHOLE input list — a reply may sit outside the
 * other active filters and must still count.
 */
function keysWithReplies(particles: TriageFilterable[]): Set<string> {
    const byThread = new Map<string, TriageFilterable[]>();
    for (const particle of particles || []) {
        const thread = (particle?.threadId || '').trim();
        if (!thread) continue;
        const group = byThread.get(thread);
        if (group) group.push(particle);
        else byThread.set(thread, [particle]);
    }

    const replied = new Set<string>();
    for (const group of byThread.values()) {
        if (group.length < 2) continue;
        const newest = Math.max(...group.map(dateSeconds));
        for (const particle of group) {
            if (dateSeconds(particle) < newest && particle.particleKey) replied.add(particle.particleKey);
        }
    }
    return replied;
}

/** True when at least one primitive would narrow the list. */
export function isTriageFilterActive(state: TriageFilterState): boolean {
    return countActiveTriageFilters(state) > 0;
}

/** The Find button's badge: one per active primitive (each source/person counts). */
export function countActiveTriageFilters(state: TriageFilterState): number {
    let count = 0;
    if ((state?.text || '').trim()) count++;
    if (state?.read && state.read !== 'any') count++;
    count += state?.sources?.length ?? 0;
    if (state?.checklistDone) count++;
    if (state?.checklistMissed) count++;
    if (state?.hasAttachments) count++;
    if (state?.hasReply) count++;
    if (state?.olderThan) count++;
    count += state?.fromEmails?.length ?? 0;
    return count;
}

/**
 * The filtered set. Returns the INPUT ARRAY ITSELF when no filter is active —
 * the component calls this from a template getter, so a fresh array per change
 * detection would thrash `*cdkVirtualFor`.
 */
export function filterParticlesForTriage<T extends TriageFilterable>(
    particles: T[],
    state: TriageFilterState,
    now: number = Date.now()
): T[] {
    if (!particles?.length) return particles || [];
    if (!isTriageFilterActive(state)) return particles;

    const text = (state.text || '').trim();
    const sources = state.sources ?? [];
    const fromEmails = state.fromEmails ?? [];

    const bySource = sources.length ? filterParticlesBySource(particles, new Set(sources)) : particles;
    // Thread linkage reads the WHOLE input, not the already-filtered view — a
    // reply hidden by another filter must still make its parent match.
    const replied = state.hasReply ? keysWithReplies(particles) : null;

    return bySource.filter((particle) => {
        if (!particle) return false;
        return (
            matchesText(particle, text) &&
            matchesRead(particle, state.read) &&
            matchesChecklist(particle, state.checklistDone, state.checklistMissed) &&
            (!state.hasAttachments || matchesAttachments(particle)) &&
            (!replied || (!!particle.particleKey && replied.has(particle.particleKey))) &&
            matchesOlderThan(particle, state.olderThan, now) &&
            matchesFrom(particle, fromEmails)
        );
    });
}

/**
 * Live counts for every toggle in the Find surface: what the list WOULD hold
 * with that toggle on, alongside whatever else is already set. A toggle that is
 * already on simply reports the current match count.
 */
export function triageToggleCounts(
    particles: TriageFilterable[],
    state: TriageFilterState,
    now: number = Date.now()
): Record<string, number> {
    const count = (patch: Partial<TriageFilterState>) =>
        filterParticlesForTriage(particles || [], { ...state, ...patch }, now).length;

    const counts: Record<string, number> = {
        unread: count({ read: 'unread' }),
        read: count({ read: 'read' }),
        checklistDone: count({ checklistDone: true }),
        checklistMissed: count({ checklistMissed: true }),
        hasAttachments: count({ hasAttachments: true }),
        hasReply: count({ hasReply: true }),
    };

    for (const option of OLDER_THAN_LABELS) {
        counts[`older:${option.key}`] = count({ olderThan: option.key });
    }
    for (const option of SOURCE_FILTER_OPTIONS) {
        const sources = state.sources?.includes(option.key) ? state.sources : [...(state.sources ?? []), option.key];
        counts[`source:${option.key}`] = count({ sources });
    }

    return counts;
}

/** Active filters as removable chips, in a stable display order. */
export function describeTriageFilters(state: TriageFilterState): TriageChip[] {
    const chips: TriageChip[] = [];
    const text = (state?.text || '').trim();
    if (text) chips.push({ id: 'text', label: `"${text}"` });
    if (state?.read === 'unread') chips.push({ id: 'read', label: 'Unread' });
    if (state?.read === 'read') chips.push({ id: 'read', label: 'Read' });

    for (const key of state?.sources ?? []) {
        const option = SOURCE_FILTER_OPTIONS.find((o) => o.key === key);
        chips.push({ id: `source:${key}`, label: option?.label ?? key });
    }

    if (state?.checklistDone) chips.push({ id: 'checklistDone', label: 'Checklist done' });
    if (state?.checklistMissed) chips.push({ id: 'checklistMissed', label: 'Missed deadline' });
    if (state?.hasAttachments) chips.push({ id: 'hasAttachments', label: 'Has attachments' });
    if (state?.hasReply) chips.push({ id: 'hasReply', label: 'Has a reply' });

    if (state?.olderThan) {
        const option = OLDER_THAN_LABELS.find((o) => o.key === state.olderThan);
        chips.push({ id: 'olderThan', label: `Older than ${option?.label ?? state.olderThan}` });
    }

    for (const email of state?.fromEmails ?? []) {
        chips.push({ id: `from:${email}`, label: email });
    }

    return chips;
}

/** Drop one chip. Always returns a NEW state — the caller's may be frozen. */
export function removeTriageFilter(state: TriageFilterState, chipId: string): TriageFilterState {
    const next: TriageFilterState = {
        ...state,
        sources: [...(state?.sources ?? [])],
        fromEmails: [...(state?.fromEmails ?? [])],
    };

    if (chipId === 'text') next.text = '';
    else if (chipId === 'read') next.read = 'any';
    else if (chipId === 'checklistDone') next.checklistDone = false;
    else if (chipId === 'checklistMissed') next.checklistMissed = false;
    else if (chipId === 'hasAttachments') next.hasAttachments = false;
    else if (chipId === 'hasReply') next.hasReply = false;
    else if (chipId === 'olderThan') next.olderThan = null;
    else if (chipId.startsWith('source:')) {
        const key = chipId.slice('source:'.length);
        next.sources = next.sources.filter((s) => s !== key);
    } else if (chipId.startsWith('from:')) {
        const email = chipId.slice('from:'.length);
        next.fromEmails = next.fromEmails.filter((e) => e !== email);
    }

    return next;
}

/**
 * What "Select all N matches" actually selects: the filtered set minus the
 * system connect-email card, which is a UI affordance, not a particle — bulk
 * File/Delete must never reach it.
 */
export function selectableTriageKeys(
    particles: TriageFilterable[],
    state: TriageFilterState,
    now: number = Date.now()
): string[] {
    return filterParticlesForTriage(particles || [], state, now)
        .filter((p) => p && (p.particleType || '').toLowerCase() !== 'system')
        .map((p) => p.particleKey || '')
        .filter(Boolean);
}
